import { Fragment, useState } from "react";
import { Box, Button, Grid, Typography, useTheme } from "@mui/material";
import { IntegrationRecommendedPolicies } from "../../components/IntegrationRecommendedPolicies";
import {
  ChevronRight as ChevronRightIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { ContentFlag } from "~/components/content-flag";
import { Code } from "~/components/code";
import { Markdown } from "~/components/markdown";
import { Command } from "~/components/guides/components";
import { useToken } from "~/providers/token";
import {
  CiCdConfigExample,
  getCicdInstructions,
} from "~/pages/integrations/IntegrationsSetup/utils/cicdInstructionUtils";
import { AnimatePresence, motion } from "motion/react";
import {
  AzureIcon,
  CircleCiIcon,
  DockerIcon,
  GithubIcon,
  GitlabIcon,
  JenkinsIcon,
  KubernetesIcon,
  TerraformIcon,
  WorkstationIcon,
} from "~/icons";
import { OrganizationScope, SpaceScope } from "~/hooks/useScope";

interface AddCiCdIntegrationProps {
  scope: SpaceScope | OrganizationScope;
  type: string;
}

export function IntegrationSetupAddCiCd({
  scope,
  type,
}: AddCiCdIntegrationProps) {
  const theme = useTheme();
  const { exchangedToken } = useToken({ autoExchange: true });
  const [view, setView] = useState<string>("setup");
  const [selectedConfig, setSelectedConfig] = useState<number>(0);

  const instructionSet = getCicdInstructions(type);
  const { title, flags, tagline, instructions } = instructionSet;

  return (
    <Fragment>
      {view === "setup" ? (
        <Fragment>
          <IntegrationAddHeader {...{ flags, title, type, tagline }} />
          <Box>
            {instructions.map((x) => {
              return (
                <Box pb={4} key={x.id}>
                  <Box sx={{ display: "inline-flex" }}>
                    <Command
                      number={x.id}
                      options={{
                        fontSize: { xs: 16 },
                        dotColor: theme.palette.background.lightest,
                      }}
                    >
                      {x.tagline}
                    </Command>
                  </Box>
                  <Box pl={6}>
                    {x.token && exchangedToken && (
                      <Fragment>
                        <Code className="bash" copyButton dataId="something">
                          {window.btoa(JSON.stringify(exchangedToken))}
                        </Code>
                      </Fragment>
                    )}
                    {x.link && x.href && (
                      <Box mt={3}>
                        <Button
                          href={x.href}
                          target="_blank"
                          variant="outlined"
                          color="secondary"
                          endIcon={<OpenInNewIcon />}
                          sx={{
                            py: 0.5,
                            px: 1,
                          }}
                        >
                          {x.link}
                        </Button>
                      </Box>
                    )}
                    {x.typography && (
                      <Box>
                        <Typography color="text.secondary" sx={{ pt: 3 }}>
                          {x.typography}
                        </Typography>
                      </Box>
                    )}
                    {x.markdownText && (
                      <Markdown
                        source={x.markdownText}
                        trusted={true}
                        sx={{
                          color: theme.palette.text.secondary,
                          "& a": {
                            color: theme.palette.secondary.light,
                          },
                        }}
                      />
                    )}
                    {x.markdown && (
                      <Box className="integration">
                        <Markdown className="integration" source={x.markdown} />
                      </Box>
                    )}
                    {x.configExamples && (
                      <Box mt={3}>
                        <Box>
                          {x.configExamples.map((example, i) => (
                            <ConfigSelectorButton
                              key={example.id}
                              item={example}
                              itemIndex={i}
                              onClick={() => setSelectedConfig(i)}
                              selectedConfig={selectedConfig}
                            />
                          ))}
                        </Box>
                        <AnimatePresence>
                          {x.configExamples.map((example, i) => (
                            <Fragment key={example.id}>
                              {selectedConfig === i && (
                                <Box
                                  component={motion.div}
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                >
                                  <Markdown
                                    className="integration"
                                    source={example.config}
                                  />
                                </Box>
                              )}
                            </Fragment>
                          ))}
                        </AnimatePresence>
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "end" }}>
            <Button
              variant="contained"
              color="primary"
              endIcon={<ChevronRightIcon />}
              onClick={() => setView("add scans")}
            >
              start scanning
            </Button>
          </Box>
        </Fragment>
      ) : (
        <IntegrationRecommendedPolicies {...{ scope }} filterTypes={[type]} />
      )}
    </Fragment>
  );
}

type Flag = {
  id: string;
  title: string;
};

interface IntegrationAddHeaderProps {
  type: string;
  title: string;
  tagline: string;
  flags: Flag[];
}

const IntegrationAddHeader = ({
  flags,
  type,
  title,
  tagline,
}: IntegrationAddHeaderProps) => {
  const getIconFromTitle = (type: string) => {
    const iconSx = { fontSize: 56 };

    switch (type.toLowerCase()) {
      case "azure":
        return <AzureIcon sx={iconSx} />;
      case "circleci":
        return <CircleCiIcon sx={iconSx} />;
      case "github":
        return <GithubIcon sx={iconSx} />;
      case "gitlab":
        return <GitlabIcon sx={iconSx} />;
      case "jenkins":
        return <JenkinsIcon sx={iconSx} />;
      default:
        return <WorkstationIcon sx={{ fontSize: 56 }} />;
    }
  };

  const getDocsLinkFromTitle = (type: string) => {
    switch (type.toLowerCase()) {
      case "azure":
        return "https://mondoo.com/docs/infra/supply/cicd/azure-pipelines";
      case "circleci":
        return "https://mondoo.com/docs/infra/supply/cicd/circleci";
      case "github":
        return "https://mondoo.com/docs/infra/supply/cicd/github-actions";
      case "gitlab":
        return "https://mondoo.com/docs/infra/supply/cicd/gitlab";
      case "jenkins":
        return "https://mondoo.com/docs/infra/supply/cicd/jenkins";
      default:
        return "https://mondoo.com/docs";
    }
  };

  return (
    <Box>
      <Grid
        container
        sx={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Grid
          sx={{
            display: "flex",
            alignItems: "flex-start",
            mb: {
              xs: 2,
            },
          }}
          size={{
            xs: 12,
            sm: 8,
          }}
        >
          {getIconFromTitle(type)}
          <Box ml={3}>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{ textTransform: "uppercase" }}
            >
              {title}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: -2, textTransform: "uppercase" }}
            >
              {tagline}
            </Typography>
          </Box>
        </Grid>
        <Grid
          sx={{
            textAlign: { xs: "left", sm: "right" },
          }}
          size={{
            xs: 12,
            sm: 3,
          }}
        >
          <Button
            href={getDocsLinkFromTitle(type)}
            target="_blank"
            variant="outlined"
            color="secondary"
            endIcon={<OpenInNewIcon />}
            sx={{
              py: 0.5,
              px: 1,
            }}
          >
            Documentation
          </Button>
        </Grid>
      </Grid>
      <Box mb={5}>
        {flags.map((flag) => (
          <ContentFlag
            flag={flag.title}
            key={flag.id}
            layout={{ mr: 2, mb: { xs: 1, sm: 0 } }}
          />
        ))}
      </Box>
    </Box>
  );
};

interface ConfigSelectorProps {
  item: CiCdConfigExample;
  itemIndex: number;
  selectedConfig: number;
  onClick: () => void;
}

const ConfigSelectorButton = ({
  item,
  itemIndex,
  selectedConfig,
  onClick,
}: ConfigSelectorProps) => {
  const getIcon = (id: string) => {
    const sxOptions = {
      mr: 1,
    };

    switch (id) {
      case "docker_config":
        return <DockerIcon sx={sxOptions} />;
      case "kubernetes_config":
        return <KubernetesIcon sx={sxOptions} />;
      case "terraform_config":
        return <TerraformIcon sx={sxOptions} />;
      default:
        return <WorkstationIcon sx={sxOptions} />;
    }
  };

  const getTitle = (id: string) => {
    switch (id) {
      case "docker_config":
        return "Docker";
      case "kubernetes_config":
        return "Kubernetes";
      case "terraform_config":
        return "Terraform";
      default:
        return "Example Config";
    }
  };

  return (
    <Box
      component={Button}
      sx={{
        display: {
          xs: "flex",
          sm: "inline-flex",
        },
        mr: 2,
        mb: {
          xs: 2,
          sm: 0,
        },
        px: 2,
        py: 1,
        background: (theme) => theme.palette.background.paper,
        boxShadow: (theme) =>
          selectedConfig === itemIndex
            ? `0 0 0 1px ${theme.palette.primary.main}`
            : "",
      }}
      onClick={onClick}
    >
      {getIcon(item.id)}
      <Typography fontWeight={700}>{getTitle(item.id)}</Typography>
    </Box>
  );
};
